import {
  Collapsible as CollapsibleRoot,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@radix-ui/react-collapsible'
import clsx from 'clsx'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import React, { useState } from 'react'

import { SolidAlertCircleIcon, SolidCircleCheckIcon } from 'slab'
import type { MemberOnboardingProviderInNetworkMapInNetwork } from 'utils/web_api.schemas'

interface ProviderProps {
  children: React.ReactNode;
  inNetworkStatus: MemberOnboardingProviderInNetworkMapInNetwork;
  lookupUrl: string;
}

function Provider ({ children, inNetworkStatus, lookupUrl }: ProviderProps) {
  function renderNetworkStatus () {
    if (inNetworkStatus === 'IN_NETWORK') {
      return <span>In-network</span>
    } else {
      return (
        <a
          className='font-semibold text-gray-800 hover:text-gray-800 border-b-2 border-dotted border-gray-400 hover:border-gray-800 transition outline outline-2 outline-offset-2 outline-transparent focus-visible:outline-thatch-500'
          href={lookupUrl}
          target='_blank'
          rel='noopener noreferrer'
        >
          Check carrier
        </a>
      )
    }
  }

  function renderIndicator () {
    if (inNetworkStatus === 'IN_NETWORK') {
      return (
        <SolidCircleCheckIcon
          aria-hidden
          className='text-green-400'
          size={16}
        />
      )
    } else {
      return (
        <SolidAlertCircleIcon
          aria-hidden
          className='text-yellow-400'
          size={16}
        />
      )
    }
  }

  return (
    <li className='flex justify-between body-sm leading-snug text-gray-800 gap-2'>
      <div className='flex items-center gap-1 grow min-w-0'>
        <div className='shrink-0'>
          {renderIndicator()}
        </div>
        <span className='truncate'>{children}</span>
      </div>
      <div className='shrink-0'>{renderNetworkStatus()}</div>
    </li>
  )
}

interface ProvidersSummaryProps {
  align?: 'start' | 'end';
  children: React.ReactNode;
  coverageIsGood: boolean;
  defaultOpen?: boolean;
  label: React.ReactNode;
}

export function ProvidersSummary ({
  align = 'start',
  children,
  coverageIsGood,
  defaultOpen,
  label,
}: ProvidersSummaryProps) {
  const [open, setOpen] = useState(defaultOpen ?? false)

  return (
    <CollapsibleRoot className='flex flex-col' open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          className={clsx(
            'group/provider-trigger',
            'flex gap-1 items-center body-bold text-gray-500 hover:text-gray-800 focus-visible:text-gray-800 rounded-md',
            'outline outline-2 outline-offset-2 outline-transparent focus-visible:outline-thatch-500',
            'transition-all',
            {
              'self-end': align === 'end',
              'mb-2': open,
              'mb-0': !open,
            }
          )}
          onClick={() => {
            setOpen((prevOpen) => !prevOpen)
          }}
          type='button'
        >
          {coverageIsGood
            ? (
              <SolidCircleCheckIcon aria-hidden className='text-green-400' size={16} />
              )
            : (
              <SolidAlertCircleIcon aria-hidden className='text-yellow-400' size={16} />
              )}
          <span>{label}</span>
          <span className='text-gray-400 group-hover/provider-trigger:text-gray-500 group-focus-visible/provider-trigger:text-gray-500 transition'>
            {open ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className='border border-gray-200 rounded-lg bg-gray-50 data-[state=open]:animate-collapsible-open data-[state=closed]:animate-collapsible-close'>
        <ul className='flex flex-col gap-2 p-2'>{children}</ul>
      </CollapsibleContent>
    </CollapsibleRoot>
  )
}

ProvidersSummary.Provider = Provider
